import { useTranslation } from 'react-i18next';

import { isEmpty } from 'lodash';

import { CustomModal } from 'components/shared';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { usePalletsTypes } from 'constants/palletTypes';
import { useModal } from 'hooks/useModal';
import { Charge, OrderDetailsDTO, Pallet } from 'interfaces';
import { HTTPService } from 'service';
import { Banner } from 'storybook';
import { logNetworkError } from 'utils/logNetworkError';

import styles from './SendToSettlementModal.module.css';

type SendToSettlementModalProps = {
   onSendForSettlementSuccess: (updatedOrder: OrderDetailsDTO) => void;
   orderId: string;
   remainingPalletsToBeSend?: Pallet[];
   additionalCharges: Charge[];
   opened: boolean;
   onClose: () => void;
};

const SendToSettlementModal = ({
   onSendForSettlementSuccess,
   orderId,
   additionalCharges,
   remainingPalletsToBeSend,
   opened,
   onClose,
}: SendToSettlementModalProps) => {
   const { t } = useTranslation();
   const { isLoading, errorInfo, setIsLoading, setErrorInfo, closeModalHandler } = useModal({
      onClose,
   });

   const { PALLET_TYPE_LABELS } = usePalletsTypes();

   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const sendOrderForSettlement = () => {
      setIsLoading(true);
      HTTPService.submitOrderForBilling(orderId)
         .then(({ data: responseData }) => {
            closeModalHandler();
            onSendForSettlementSuccess(responseData);
         })
         .catch(err => {
            setErrorInfo(DEFAULT_ERROR_DESCRIPTIONS.UNKNOWN);
            logNetworkError(err);
         })
         .finally(() => setIsLoading(false));
   };

   const withAdditionalCharges = !isEmpty(additionalCharges);
   const withRemainingPalletsToBeSend = !isEmpty(remainingPalletsToBeSend);

   return (
      <CustomModal
         opened={opened}
         onClose={closeModalHandler}
         title={t('sendToSettlement.title')}
         {...(withAdditionalCharges && !withRemainingPalletsToBeSend
            ? {
                 innerTitle: t('common.warning'),
                 icon: 'ALERT_TRIANGLE',
              }
            : {})}
         primaryButtonProps={{
            text:
               !withRemainingPalletsToBeSend && !withAdditionalCharges
                  ? t('sendToSettlement.title')
                  : t('sendToSettlement.primaryButtonWithCharges'),
            variant: 'filled-primary',
            onClick: sendOrderForSettlement,
         }}
         secondaryButtonProps={{
            text: t('common.cancel'),
            variant: 'text',
            onClick: closeModalHandler,
         }}
         isLoading={isLoading}
         error={errorInfo}
         size={!withRemainingPalletsToBeSend && !withAdditionalCharges ? 600 : 700}
         modalContentClassName={styles.modalContent}
         leftSideModalContent
      >
         <>
            {withAdditionalCharges && !withRemainingPalletsToBeSend && (
               <p>{t('sendToSettlement.onlyAdditionalCharges')}</p>
            )}
            {withRemainingPalletsToBeSend && (
               <div className={styles.remainingPalletsContainer}>
                  <Banner
                     fullWidth
                     children={t('sendToSettlement.remainingPalletsBanner')}
                     variant="error"
                  />
                  <p>{t('sendToSettlement.remainingPalletsLeft')}</p>
                  <ul>
                     {remainingPalletsToBeSend?.map((pallet, index) => (
                        <li key={index}>
                           {PALLET_TYPE_LABELS[pallet.palletType]}{' '}
                           <strong>
                              {pallet.amount} {t('common.pieces')}
                           </strong>
                        </li>
                     ))}
                  </ul>
                  <p className={styles.remainingPalletsText}>
                     {t('sendToSettlement.missingPalletsText')}
                  </p>
                  {withAdditionalCharges && (
                     <p className={styles.remainingPalletsText}>
                        {t('sendToSettlement.alsoHasAdditionalCharges')}
                     </p>
                  )}
               </div>
            )}
            {!withRemainingPalletsToBeSend && !withAdditionalCharges && (
               <p>{t('sendToSettlement.allFine')}</p>
            )}
            <p>{t('sendToSettlement.confirmationQuestion')}</p>
         </>
      </CustomModal>
   );
};

export default SendToSettlementModal;
